import { formatDateTime, isoMonthSchema, monthOf } from "@pcj/shared";
import { History } from "lucide-react";
import { Navigate, useParams } from "react-router";
import { type MonthFolder, MonthFolders, MonthHeader } from "../../components/MonthFolders";
import { Banner, Button, EmptyState, SectionTitle, Spinner, Table, td, th } from "../../components/ui";
import { errorMessage } from "../../lib/api";
import { AUDIT_ACTION_LABELS, auditDetail, plural } from "../../lib/format";
import { useAuditLogMonth, useAuditMonths } from "../../lib/queries";
import { useCompany } from "./CompanyLayout";

const SUBTITLE = "Quién hizo qué y cuándo. No se puede editar ni borrar.";

/** The audit log (Bitácora) in one folder per month. */
export function AuditLogTab() {
  const { companyId, today } = useCompany();
  const months = useAuditMonths(companyId);

  const folders: MonthFolder[] = (months.data ?? []).map(({ month, entries }) => ({
    month,
    title: plural(entries, "movimiento", "movimientos"),
  }));

  return (
    <div className="space-y-4">
      <SectionTitle title="Bitácora" subtitle={`Una carpeta por mes. ${SUBTITLE}`} />
      {months.isPending ? (
        <Spinner label="Cargando bitácora…" />
      ) : months.isError ? (
        <Banner tone="error">{errorMessage(months.error)}</Banner>
      ) : folders.length === 0 ? (
        <EmptyState icon={History} title="Sin movimientos todavía" />
      ) : (
        <MonthFolders
          folders={folders}
          currentMonth={monthOf(today)}
          yearSummary={(year) => {
            const total = (months.data ?? [])
              .filter((m) => m.month.startsWith(String(year)))
              .reduce((sum, m) => sum + m.entries, 0);
            return plural(total, "movimiento", "movimientos");
          }}
        />
      )}
    </div>
  );
}

/** One month of the audit log (route `audit-log/:month`), loaded a page at a time. */
export function AuditLogMonthPage() {
  const { month = "" } = useParams();
  const { companyId } = useCompany();
  const valid = isoMonthSchema.safeParse(month).success;
  const log = useAuditLogMonth(companyId, month, valid);
  if (!valid) return <Navigate to=".." relative="path" replace />;

  const entries = log.data?.pages.flat() ?? [];
  return (
    <div className="space-y-4">
      <MonthHeader month={month} title="Bitácora de" subtitle={SUBTITLE} />
      {log.isPending ? (
        <Spinner label="Cargando bitácora…" />
      ) : log.isError ? (
        <Banner tone="error">{errorMessage(log.error)}</Banner>
      ) : entries.length === 0 ? (
        <EmptyState icon={History} title="Sin movimientos en este mes" />
      ) : (
        <>
          <Table>
            <thead className="bg-gray-50">
              <tr>
                <th className={th}>Fecha</th>
                <th className={th}>Usuario</th>
                <th className={th}>Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {entries.map((entry) => {
                const detail = auditDetail(entry);
                return (
                  <tr key={entry.id}>
                    <td className={`${td} whitespace-nowrap text-gray-600`}>{formatDateTime(entry.createdAt)}</td>
                    {/* No user: a change made on the server (CLI). */}
                    <td className={`${td} text-gray-700`}>{entry.userName ?? (entry.userId ? "—" : "Sistema")}</td>
                    <td className={`${td} text-gray-700`}>
                      <History className="mr-1.5 inline h-3.5 w-3.5 text-gray-400" aria-hidden />
                      {AUDIT_ACTION_LABELS[entry.action] ?? entry.action}
                      {detail && <span className="text-gray-500"> · {detail}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          {log.hasNextPage && (
            <Button variant="secondary" loading={log.isFetchingNextPage} onClick={() => log.fetchNextPage()}>
              Cargar más movimientos
            </Button>
          )}
        </>
      )}
    </div>
  );
}
